// FORFEO INC. - Lot 4R-A
//
// Contrat canonique du modele de disponibilite Forfeo Reserve.
// Ce module est volontairement pur: aucun appel reseau, Supabase ou Stripe.

export const FORFEO_RESERVE_CONTRACT_VERSION =
  'forfeo-reserve-availability-4r-a-v1' as const;

export const FORFEO_RESERVE_AUTHORITIES = {
  discovery: 'EXPLORE',
  availability: 'RESERVE',
  financial: 'GIFTSHOP',
  mobile: 'CONSUMER_ONLY',
} as const;

export const FORFEO_RESERVE_RESOURCE_KINDS = [
  'establishment',
  'room',
  'table',
  'treatment',
  'activity',
  'ticket_inventory',
  'vehicle',
  'equipment',
  'employee',
  'service',
  'appointment',
  'venue',
] as const;

export type ForfeoReserveResourceKind =
  (typeof FORFEO_RESERVE_RESOURCE_KINDS)[number];

export const FORFEO_RESERVE_CAPACITY_UNITS = [
  'person',
  'seat',
  'room',
  'table',
  'ticket',
  'vehicle',
  'equipment',
  'appointment',
  'unit',
] as const;

export type ForfeoReserveCapacityUnit =
  (typeof FORFEO_RESERVE_CAPACITY_UNITS)[number];

export const FORFEO_RESERVE_INVENTORY_MODES = [
  'internal',
  'external_adapter',
  'hybrid',
] as const;

export type ForfeoReserveInventoryMode =
  (typeof FORFEO_RESERVE_INVENTORY_MODES)[number];

export const FORFEO_RESERVE_PROVIDER_KINDS = [
  'internal',
  'calcom',
  'qloapps',
  'hoteldruid',
  'ical',
  'partner_api',
] as const;

export type ForfeoReserveProviderKind =
  (typeof FORFEO_RESERVE_PROVIDER_KINDS)[number];

export const FORFEO_RESERVE_RULE_KINDS = [
  'weekly_recurring',
  'date_range',
  'season',
  'holiday',
] as const;

export type ForfeoReserveRuleKind =
  (typeof FORFEO_RESERVE_RULE_KINDS)[number];

export const FORFEO_RESERVE_EXCEPTION_KINDS = [
  'open',
  'blocked',
  'capacity_override',
] as const;

export type ForfeoReserveExceptionKind =
  (typeof FORFEO_RESERVE_EXCEPTION_KINDS)[number];

export const FORFEO_RESERVE_AVAILABILITY_STATES = [
  'unknown',
  'available',
  'limited',
  'held',
  'fully_booked',
  'blocked',
  'closed',
  'unavailable',
] as const;

export type ForfeoReserveAvailabilityState =
  (typeof FORFEO_RESERVE_AVAILABILITY_STATES)[number];

export const FORFEO_RESERVE_RESERVATION_STATES = [
  'draft',
  'hold',
  'pending_confirmation',
  'pending_payment',
  'confirmed',
  'completed',
  'cancelled',
  'declined',
  'expired',
  'no_show',
  'refunded',
  'manual_review',
] as const;

export type ForfeoReserveReservationState =
  (typeof FORFEO_RESERVE_RESERVATION_STATES)[number];

export const FORFEO_RESERVE_REQUIREMENT_STRATEGIES = [
  'all_required',
  'one_of',
  'capacity_pool',
] as const;

export type ForfeoReserveRequirementStrategy =
  (typeof FORFEO_RESERVE_REQUIREMENT_STRATEGIES)[number];

export interface ForfeoReserveLocalizedText {
  fr: string;
  en?: string;
}

export interface ForfeoReserveCatalogItemContract {
  id: string;
  businessId: string;
  kind: ForfeoReserveResourceKind;
  name: ForfeoReserveLocalizedText;
  description?: ForfeoReserveLocalizedText;
  timezone: string;
  inventoryMode: ForfeoReserveInventoryMode;
  provider: ForfeoReserveProviderKind;
  defaultDurationMinutes?: number;
  minimumPartySize: number;
  maximumPartySize: number;
  capacityUnit: ForfeoReserveCapacityUnit;
  active: boolean;
  sourceSystem: string;
  sourceId?: string;
}

export interface ForfeoReserveResourceContract {
  id: string;
  businessId: string;
  parentResourceId?: string;
  kind: ForfeoReserveResourceKind;
  name: ForfeoReserveLocalizedText;
  timezone: string;
  capacityTotal: number;
  capacityUnit: ForfeoReserveCapacityUnit;
  active: boolean;
  sourceSystem: string;
  sourceId?: string;
}

export interface ForfeoReserveLocalWindow {
  timezone: string;
  localStartTime: string;
  localEndTime: string;
  daysOfWeek: readonly number[];
  activeFrom?: string;
  activeUntil?: string;
}

export interface ForfeoReserveInstantWindow {
  startsAt: string;
  endsAt: string;
}

export interface ForfeoReserveAvailabilityRuleContract {
  id: string;
  businessId: string;
  catalogItemId?: string;
  resourceId?: string;
  kind: ForfeoReserveRuleKind;
  localWindow: ForfeoReserveLocalWindow;
  capacityOverride?: number;
  active: boolean;
}

export interface ForfeoReserveAvailabilityExceptionContract {
  id: string;
  businessId: string;
  catalogItemId?: string;
  resourceId?: string;
  kind: ForfeoReserveExceptionKind;
  window: ForfeoReserveInstantWindow;
  capacityOverride?: number;
  reasonCode?: string;
  idempotencyKey: string;
}

export interface ForfeoReserveAvailabilityQuery {
  catalogItemId: string;
  startsAt: string;
  endsAt: string;
  partySize: number;
  timezone: string;
  correlationId: string;
}

export interface ForfeoReserveAvailabilityResult {
  state: ForfeoReserveAvailabilityState;
  catalogItemId: string;
  startsAt: string;
  endsAt: string;
  requestedCapacity: number;
  remainingCapacity?: number;
  timezone: string;
  evaluatedAt: string;
  authority: 'RESERVE';
  correlationId: string;
}

export const FORFEO_RESERVE_FORBIDDEN_CLIENT_FIELDS = [
  'amount',
  'amount_cents',
  'price',
  'price_cents',
  'currency',
  'tax',
  'tax_cents',
  'fee',
  'fee_cents',
  'deposit',
  'deposit_cents',
  'paid',
  'payment_status',
  'stripe_session_id',
  'stripe_payment_intent_id',
  'refund_amount',
  'refund_amount_cents',
] as const;

export type ForfeoReserveForbiddenClientField =
  (typeof FORFEO_RESERVE_FORBIDDEN_CLIENT_FIELDS)[number];

const forbiddenClientFieldSet = new Set<string>(
  FORFEO_RESERVE_FORBIDDEN_CLIENT_FIELDS,
);

const ISO_LOCAL_TIME = /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

export function isForfeoReserveResourceKind(
  value: unknown,
): value is ForfeoReserveResourceKind {
  return FORFEO_RESERVE_RESOURCE_KINDS.includes(
    value as ForfeoReserveResourceKind,
  );
}

export function isForfeoReserveCapacityUnit(
  value: unknown,
): value is ForfeoReserveCapacityUnit {
  return FORFEO_RESERVE_CAPACITY_UNITS.includes(
    value as ForfeoReserveCapacityUnit,
  );
}

export function isForfeoReserveIanaTimezone(
  value: unknown,
): value is string {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  try {
    new Intl.DateTimeFormat('en-CA', {
      timeZone: value,
    }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}

export function normalizeForfeoReserveCapacity(
  value: unknown,
  maximum = 100_000,
): number | null {
  const numeric = Number(value);

  if (
    !Number.isSafeInteger(numeric)
    || numeric < 1
    || numeric > maximum
  ) {
    return null;
  }

  return numeric;
}

export function isForfeoReserveLocalTime(
  value: unknown,
): value is string {
  return typeof value === 'string'
    && ISO_LOCAL_TIME.test(value);
}

export function isForfeoReserveHalfOpenInterval(
  startsAt: unknown,
  endsAt: unknown,
): boolean {
  const start = Date.parse(String(startsAt ?? ''));
  const end = Date.parse(String(endsAt ?? ''));

  return Number.isFinite(start)
    && Number.isFinite(end)
    && start < end;
}

export function forfeoReserveIntervalsOverlap(
  first: ForfeoReserveInstantWindow,
  second: ForfeoReserveInstantWindow,
): boolean {
  if (
    !isForfeoReserveHalfOpenInterval(
      first.startsAt,
      first.endsAt,
    )
    || !isForfeoReserveHalfOpenInterval(
      second.startsAt,
      second.endsAt,
    )
  ) {
    return false;
  }

  return Date.parse(first.startsAt) < Date.parse(second.endsAt)
    && Date.parse(second.startsAt) < Date.parse(first.endsAt);
}

export function hasForfeoReserveForbiddenClientFields(
  payload: unknown,
): boolean {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  if (Array.isArray(payload)) {
    return payload.some(
      hasForfeoReserveForbiddenClientFields,
    );
  }

  for (const [key, value] of Object.entries(payload)) {
    if (forbiddenClientFieldSet.has(key.toLowerCase())) {
      return true;
    }

    if (hasForfeoReserveForbiddenClientFields(value)) {
      return true;
    }
  }

  return false;
}

export function normalizeForfeoReserveIdempotencyKey(
  value: unknown,
): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();

  if (
    normalized.length < 16
    || normalized.length > 200
    || !/^[A-Za-z0-9:_-]+$/.test(normalized)
  ) {
    return null;
  }

  return normalized;
}

export function validateForfeoReserveLocalWindow(
  window: ForfeoReserveLocalWindow,
): readonly string[] {
  const errors: string[] = [];

  if (!isForfeoReserveIanaTimezone(window.timezone)) {
    errors.push('invalid_timezone');
  }

  if (
    !isForfeoReserveLocalTime(window.localStartTime)
    || !isForfeoReserveLocalTime(window.localEndTime)
    || window.localStartTime >= window.localEndTime
  ) {
    errors.push('invalid_local_window');
  }

  const days = [...new Set(window.daysOfWeek)];

  if (
    days.length === 0
    || days.some(
      (day) => !Number.isInteger(day) || day < 0 || day > 6,
    )
  ) {
    errors.push('invalid_days_of_week');
  }

  if (
    window.activeFrom
    && window.activeUntil
    && window.activeFrom > window.activeUntil
  ) {
    errors.push('invalid_active_date_range');
  }

  return errors;
}

export function assertForfeoReserveAuthorityBoundary(): true {
  if (
    FORFEO_RESERVE_AUTHORITIES.discovery !== 'EXPLORE'
    || FORFEO_RESERVE_AUTHORITIES.availability !== 'RESERVE'
    || FORFEO_RESERVE_AUTHORITIES.financial !== 'GIFTSHOP'
    || FORFEO_RESERVE_AUTHORITIES.mobile !== 'CONSUMER_ONLY'
  ) {
    throw new Error('forfeo_reserve_authority_boundary_invalid');
  }

  return true;
}
